// src/pages/MeetingRoom/ui/Seat.jsx
import React, { useMemo } from "react";
import LiveKitVideo from "./LiveKitVideo";
import Icon from "./Icon";

const AVATAR_GRADIENTS = [
  ["#4ADE80", "#16A34A"],
  ["#38BDF8", "#0284C7"],
  ["#FCD34D", "#D97706"],
  ["#C084FC", "#7C3AED"],
  ["#FB7185", "#BE123C"],
  ["#FB923C", "#C2410C"],
  ["#34D399", "#059669"],
  ["#A78BFA", "#5B21B6"],
];

const getAvatarColors = (name = "") => {
  if (!name || typeof name !== "string") return AVATAR_GRADIENTS[0];
  return AVATAR_GRADIENTS[name.charCodeAt(0) % AVATAR_GRADIENTS.length];
};

export default function Seat({
  seatIndex,
  pos,
  participant,
  onAvatarPress,
  onNamePress,
  onLike,
  onShowNetworking,
  isHeadSeat = false,
}) {
  const isEmpty    = !participant;
  const isSpeaking = participant?.isSpeaking  ?? false;
  const isMuted    = participant?.isMuted      ?? true;
  const isCameraOn = participant?.isCameraOn   ?? false;
  const isLocal    = participant?.isLocal      ?? false;
  const name       = participant?.name         ?? "";
  const image      = participant?.image        ?? null;
  const hasVideo   = !!participant?.hasVideo;
  const trackRef   = participant?.trackRef     ?? null;
  const netRole    = String(participant?.networkingRole  || "").trim();
  const netIntent  = String(participant?.networkingIntent || "").trim();
  const hasNetworking = !!(netRole || netIntent);
  const isInPrivateMeet = participant?.participantStatus === "in_private_meet";

  const netBadgeLetter = useMemo(() => {
    if (!netRole) return "";
    if (/seek/i.test(netRole))   return "S";
    if (/give/i.test(netRole))   return "G";
    if (/mentor/i.test(netRole)) return "G";
    return netRole[0]?.toUpperCase?.() || "";
  }, [netRole]);

  const netBadgeTitle = useMemo(() => {
    if (!hasNetworking) return "";
    return `Role: ${netRole || "Not set"}\nIntent: ${netIntent || "No intent"}`;
  }, [hasNetworking, netRole, netIntent]);

  const [c1, c2] = getAvatarColors(name);

  // Build className list cleanly
  const rootClass = [
    "jt-seat",
    pos.side,
    isSpeaking        ? "speaking"          : "",
    isEmpty           ? "empty"             : "",
    isHeadSeat        ? "jt-seat--head"     : "",
    isInPrivateMeet   ? "jt-seat--private"  : "",
  ].filter(Boolean).join(" ");

  return (
    <div
      className={rootClass}
      style={{ left: pos.left, top: pos.top }}
    >
      {!isEmpty && <div className="jt-seatHalo" />}

      <button
        className="jt-seatBtn"
        type="button"
        disabled={isEmpty}
        onClick={() => {
          if (isEmpty) return;
          if (hasVideo) onAvatarPress?.(participant);
          else          onNamePress?.(participant);
        }}
      >
        <div className="jt-seatDepth" />
        <div className="jt-seatRing">
          <div className="jt-seatCushion">
            {isEmpty ? (
              <div className="jt-seatEmpty">
                <div className="jt-seatEmptyIcon">
                  <i className="fa-regular fa-user" />
                </div>
                <div className="jt-seatEmptyNum">{seatIndex + 1}</div>
              </div>
            ) : (
              <div className="jt-seatAvatar">
                {hasVideo && trackRef ? (
                  <LiveKitVideo
                    trackRef={trackRef}
                    muted={isLocal}
                    mirror={isLocal}
                    objectFit="cover"
                  />
                ) : (
                  <div
                    className="jt-seatAvatarGrad"
                    style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}
                  >
                    {image
                      ? <img src={image} alt="" />
                      : <span>{(name?.[0] || "?").toUpperCase()}</span>
                    }
                  </div>
                )}
              </div>
            )}
            <div className="jt-seatSheen" />
          </div>
        </div>
      </button>

      {/* Like button — remote participants only */}
      {!isEmpty && !isLocal && participant?.userId && (
        <button
          type="button"
          className="jt-seatLikeBtn"
          title="Send like"
          onClick={(e) => {
            e.stopPropagation();
            onLike?.(participant.userId);
          }}
        >
          ❤️
        </button>
      )}

      {/* In Private Meet badge */}
{!isEmpty && isInPrivateMeet && (
  <div
    className="jt-seatPrivateBadge"
    title="In a private meeting"
  >
    🔒
  </div>
)}

      {/* Networking badge */}
      {!isEmpty && hasNetworking && (
        <button
          type="button"
          className="jt-seatNetBtn"
          title={netBadgeTitle}
          onClick={(e) => {
            e.stopPropagation();
            onShowNetworking?.(participant);
          }}
        >
          {netBadgeLetter || "N"}
        </button>
      )}

      {/* Mic / cam / name — occupied seat */}
      {!isEmpty && (
        <>
          <div
            className={`jt-seatMic ${isMuted ? "off" : "on"}`}
            title={isMuted ? "Muted" : "Mic on"}
          >
            {isMuted ? <Icon name="micOff" /> : <Icon name="mic" />}
          </div>

          {isCameraOn && hasVideo && (
            <div className="jt-seatCamDot" title="Camera on" />
          )}

          <button
            type="button"
            className={`jt-seatName jt-seatNameBtn ${pos.label}`}
            title={`View ${name}'s profile`}
            onClick={(e) => {
              e.stopPropagation();
              onNamePress?.(participant);
            }}
          >
            {isLocal ? "You" : name}
          </button>
        </>
      )}

      {/* Empty seat label */}
      {isEmpty && (
        <div className={`jt-seatName ${pos.label} emptyTag`}>
          {seatIndex + 1}
        </div>
      )}
    </div>
  );
}